import { Box, Stack, Typography } from '@mui/material';

import { CONTENT, getEntityType } from './utils';
import { FinancialOverviewTiles } from './AccountingWidgets';
import { deriveBookkeepingSummary } from './deriveSummary';

import { useMember } from 'modules/common/queries/member';
import { isInOnboardingStages } from 'modules/common/utils/memberStage';
import TaskCarousel from 'modules/Tasks/TaskCarousel';

/**
 * Demo replacement for modules/Dashboard/Overview/Tab. The prod home renders
 * TaxTracker widgets backed by ITR-tracker endpoints that aren't mocked. This demo
 * Home keeps the prod "Priority tasks" carousel (the member task framework, fed by
 * demo/handlers/memberTasks.ts) and adds the Financial Overview tiles — the same
 * building blocks as the Accounting Overview page (demo/AccountingWidgets.tsx).
 */

interface ContentShape {
    bookkeeping?: {
        transactions?: Parameters<typeof deriveBookkeepingSummary>[0];
    };
}

export default function DemoDashboard() {
    const { data: member } = useMember();

    // Onboarding: the prod Layout renders the real <LaunchpadTasks/>, so render nothing here to
    // avoid stacking the financial tiles beneath it.
    if (isInOnboardingStages((member as { stage?: string } | undefined)?.stage)) {
        return null;
    }

    const content = CONTENT[getEntityType(member)] as ContentShape;

    const firstName = (member?.first_name as string | undefined) ?? 'there';

    // Derived from the transaction ledger at runtime (single source of truth).
    const { ytdIncome, ytdExpenses, mtdIncome, mtdExpenses, overview } = deriveBookkeepingSummary(
        content.bookkeeping?.transactions ?? []
    );

    return (
        <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
            <Typography variant="h1" sx={{ mb: { xs: 4, md: 6 } }}>
                Welcome back, {firstName}
            </Typography>

            {/* Real prod "Priority tasks" carousel. Since HUB-1573 it reads the server-ranked feed
                (/v2/members/tasks/feed/) — mocked in demo/handlers/memberTasks.ts. */}
            <Box sx={{ mb: 6 }}>
                <TaskCarousel surface="dashboard" />
            </Box>

            <Stack spacing={3}>
                <Typography variant="h2">Financial Overview</Typography>
                <FinancialOverviewTiles
                    ytdIncome={ytdIncome}
                    ytdExpenses={ytdExpenses}
                    mtdIncome={mtdIncome}
                    mtdExpenses={mtdExpenses}
                    overview={overview}
                />
            </Stack>
        </Box>
    );
}
